import {Snackbar, Typography} from '@mui/material'
import React, {useEffect, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import TableCustom, {PaginationType} from 'src/components/elements/TableCustom/TableCustom'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import LayoutContainer from 'src/components/layouts/LayoutContainer/LayoutContainer'
import Filters from 'src/components/templates/Filters/Filters'
import {PATHS, STATUS, sortPatientsByStateAndName, sortPatientStateOptions, statesOptions} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {useHistory} from 'react-router-dom'
import {useCookies} from 'react-cookie'

// Massimo consentito dal backend per pagina: con l'ordinamento di default (per stato) i
// pazienti vengono caricati tutti insieme, ordinati e paginati lato client, come nella
// lista new-style; il backend non sa ordinare per rango di stato.
const FETCH_SIZE = 200

const buildDefaultFiltersFromConfig = (filterConfig = []) => {
  return filterConfig.reduce<Record<string, any>[]>((acc, {id, type, defaultValue}) => {
    if (defaultValue) {
      acc.push({
        property: id,
        type,
        value: defaultValue
      })
    }
    return acc
  }, [])
}

const Patients = () => {
  const pspPatientsConfigurations = useAppSelector(state => state.pspConfigReducer.data.patientsConfigurations)

  const columns = pspPatientsConfigurations.columns ?? []
  // Opzioni di stato nello stesso ordine della lista new-style (attivi prima di quelli da attivare).
  const filterConfig = (pspPatientsConfigurations.filters ?? []).map((filter: any) =>
    filter.id === 'state' && filter.options ? {...filter, options: sortPatientStateOptions(filter.options)} : filter
  )
  const currentScope = useAppSelector(state => state.pspConfigReducer.data.scope)
  const history = useHistory()
  const [cookies] = useCookies(['tokens'])
  const [filters, setFilters] = useState<Record<string, string | string[]>[]>(buildDefaultFiltersFromConfig(filterConfig))
  const [patients, setPatients] = useState<Record<string, any>[]>([])
  const [status, setStatus] = useState<string>()
  const [count, setCount] = useState<number>(0)
  const [statusCount, setStatusCount] = useState<string>()
  const [programs, setPrograms] = useState<Record<string, any>[]>([])
  const [pagination, setPagination] = useState<PaginationType>({
    page: 0,
    rowsPerPage: 10,
    order: ''
  })

  // Senza un ordinamento scelto dall'utente vale quello di default (stato, poi cognome e
  // nome) calcolato lato client; cliccando su una colonna si torna all'ordinamento e alla
  // paginazione lato server.
  const isDefaultOrder = !pagination.order
  const serverPage = isDefaultOrder ? 0 : pagination.page
  const serverRowsPerPage = isDefaultOrder ? FETCH_SIZE : pagination.rowsPerPage

  useEffect(() => {
    const fetchPatients = async () => {
      const fetchedData = await apiClient.postPatientsSearch(filters, setStatus, {
        page: serverPage,
        rowsPerPage: serverRowsPerPage,
        order: pagination.order
      }) ?? []
      const orderedData = isDefaultOrder ? sortPatientsByStateAndName(fetchedData) : fetchedData
      const parsedData = orderedData.map((patient: any) => {
        return {
          ...patient,
          state: statesOptions.find((option) => option.value === patient.state)?.label
        }
      })
      setPatients(parsedData)
      const fetchedDataCount = await apiClient.postPatientsSearchCount(filters, setStatusCount) ?? []
      setCount(fetchedDataCount)
    }

    fetchPatients()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.order, serverPage, serverRowsPerPage])

  useEffect(() => {
    const fetchPrograms = async () => {
      const fetchedPrograms = await apiClient.getPrograms()
      setPrograms(fetchedPrograms)
    }

    fetchPrograms()
  }, [])

  const visiblePatients = isDefaultOrder
    ? patients.slice(pagination.page * pagination.rowsPerPage, (pagination.page + 1) * pagination.rowsPerPage)
    : patients
  const totalElements = isDefaultOrder ? Math.min(count, patients.length) : count

  const onSubmit = (values: any) => {
    setFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const onPatientRowClick = ({_id, scope}: any) => {
    if (scope === currentScope) {
      history.push(`${PATHS.PATIENTS}/${_id}`)
    } else {
      const host = programs.find(({scope: programScope}) => programScope === scope)?.url ?? window.location.host
      const patientUrl = new URL(`${PATHS.PATIENTS}/${_id}`, host)
      window.location.href = cookies.tokens ? `${patientUrl.href}?cookies=${cookies.tokens}&scope=${scope}` : patientUrl.href
    }
  }

  return (
    <LayoutContainer
      breadcrumbs={[{label: 'Pazienti', href: PATHS.PATIENTS}]}
      buttons={[
        <Filters config={filterConfig} key='filters' onSubmit={onSubmit} />
      ]}
    >
      <div style={{marginTop: '16px'}}>
        <TableCustom
          columns={columns}
          onPaginationChange={setPagination}
          onRowClick={onPatientRowClick}
          pagination={pagination}
          rows={visiblePatients}
          totalElements={totalElements}
        />
      </div>
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        autoHideDuration={2000} onClose={() => { setStatus(undefined); setStatusCount(undefined) }}
        open={status === STATUS.REJECTED || statusCount === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom
            onClose={() => { setStatus(undefined); setStatusCount(undefined) }}
            severity='error'
            sx={{width: '100%'}}
          >
            <Typography variant='h5'>
              <FormattedMessage id='patientsWarning' />
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
    </LayoutContainer>
  )
}

export default Patients
